/**
 * Sentry Configuration for Error Tracking & Performance Monitoring
 * Includes frontend error tracking, performance monitoring, and session replay
 */

import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

// Sentry DSN should be stored as environment variable
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.MODE || "development";
const RELEASE = import.meta.env.VITE_APP_VERSION || "1.0.0";

/**
 * Initialize Sentry with React-specific integrations
 * Call this once in main.tsx before rendering app
 */
export function initSentry() {
  // Only initialize Sentry if DSN is configured
  if (!SENTRY_DSN) {
    console.warn("[Sentry] DSN not configured. Skipping initialization.");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    
    // Integrations
    integrations: [
      // React Router integration for route tracking
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      
      // Replay integration for session replay (production only)
      ...(ENVIRONMENT === "production"
        ? [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ]
        : []),
    ],

    // Performance Monitoring
    tracesSampleRate: ENVIRONMENT === "production" ? 0.1 : 1.0, // 10% in prod, 100% in dev
    
    // Session Replay (only in production)
    replaysSessionSampleRate: ENVIRONMENT === "production" ? 0.1 : 0, // 10% in prod
    replaysOnErrorSampleRate: 1.0, // Always capture when error occurs

    // Filter sensitive data
    beforeSend(event, hint) {
      // Filter out specific error types if needed
      if (event.exception) {
        const error = hint.originalException as Error;
        
        // Ignore specific errors
        if (error?.message?.includes("ResizeObserver loop")) {
          return null;
        }
        
        // Redact sensitive data from error messages
        if (event.message) {
          event.message = redactSensitiveData(event.message);
        }
      }
      
      return event;
    },

    // Custom transaction metadata
    beforeSendTransaction(transaction) {
      // Add custom context
      transaction.contexts = transaction.contexts || {};
      transaction.contexts.app = {
        version: RELEASE,
        environment: ENVIRONMENT,
      };
      
      return transaction;
    },

    // Ignore specific URLs/errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      // Random plugins/extensions
      "originalCreateNotification",
      "canvas.contentDocument",
      // Facebook borked
      "fb_xd_fragment",
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
      "bmi_SafeAddOnload",
      "EBCallBackMessageReceived",
      // See http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      "Can't find variable: ZiteReader",
      "jigsaw is not defined",
      "ComboSearch is not defined",
      // Network errors
      "NetworkError",
      "Failed to fetch",
      // Common non-actionable errors
      "ResizeObserver loop",
    ],
  });

  console.log("[Sentry] Initialized successfully");
}

/**
 * Redact sensitive data from strings
 */
function redactSensitiveData(message: string): string {
  const patterns = [
    { pattern: /\b[\w\.-]+@[\w\.-]+\.\w{2,}\b/gi, replacement: "[EMAIL]" },
    { pattern: /\b\d{3}-\d{2}-\d{4}\b/g, replacement: "[SSN]" },
    { pattern: /\b\d{16}\b/g, replacement: "[CC]" },
    { pattern: /Bearer\s+[\w-]+\.[\w-]+\.[\w-]+/gi, replacement: "Bearer [TOKEN]" },
    { pattern: /api[_-]?key[=:]\s*['\"]?[\w-]+['\"]?/gi, replacement: "api_key=[REDACTED]" },
  ];

  let redacted = message;
  patterns.forEach(({ pattern, replacement }) => {
    redacted = redacted.replace(pattern, replacement);
  });

  return redacted;
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: { id: string; email?: string; role?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Manually capture exception with context
 */
export function captureException(
  error: Error,
  context?: Record<string, any>
) {
  if (context) {
    Sentry.setContext("custom", context);
  }
  
  Sentry.captureException(error);
}

/**
 * Capture message (non-error logs)
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, any>
) {
  if (context) {
    Sentry.setContext("custom", context);
  }
  
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging context
 */
export function addBreadcrumb(
  message: string,
  category: string,
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, any>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

/**
 * Start a performance span
 */
export function startSpan(name: string, op: string) {
  return Sentry.startSpan({ name, op }, (span) => span);
}

/**
 * Wrap component with Sentry profiler
 */
export const withSentryProfiler = Sentry.withProfiler;
